import React from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { renderEventMatchingIcon } from 'components/Calendaring';
import { type CIReferral } from 'components/CriticalIncident/interfaces';
import { type ColumnDefinition } from 'components/Shared/MaterialTable';
import useTimezoneUtils from 'hooks/useTimezoneUtils';
import { WorkOrderCell } from '../CIReferralsTable/CIReferralsTableCells/WorkOrderCell';
import { PriorityCell } from '../CIReferralsTable/CIReferralsTableCells/PriorityCell';
import { StatusCell } from '../CIReferralsTable/CIReferralsTableCells/StatusCell';
import { formatAddress } from 'components/CriticalIncident/utils/formatters';
import { useAuth, type ProviderData } from 'components/Auth';
import { toShortTimezone } from 'utils';

export const useCIReferralsListColumnDefinitions = (): ColumnDefinition[] => {
    const { t } = useTranslation('criticalIncident', { useSuspense: false });
    const { effectiveProviderData = {} as ProviderData } = useAuth();
    const timezoneSuffix = toShortTimezone(effectiveProviderData?.timeZone ?? '');

    const { localeTimeFormat, formatDate } = useTimezoneUtils();

    const renderWorkOrderID = (referral: CIReferral) => <WorkOrderCell referral={referral} />;

    const renderStatus = (referral: CIReferral) => <StatusCell status={referral.status} />;

    const renderPriority = (referral: CIReferral) => <PriorityCell priority={referral.priority} />;

    const renderModality = (referral: CIReferral) => {
      const { city, state } = { ...referral?.location };
      return (
        <div className="ci_case_table_detail">
            {renderEventMatchingIcon(referral.modality)}
            <span>
                {['inperson', 'in person'].includes(referral.modality.toLowerCase()) && (!!city || !!state)
                    ? formatAddress(referral.location, { cityAndStateOnly: true, t })
                    : t(referral.modality)}
            </span>
        </div>
    )};

    const renderStartDateAndTime = (referral: CIReferral) => (
        <span className="start_date">
            {formatDate(
                moment(referral.bookingStartTime),
                `MMM Do, ${localeTimeFormat}`
            )}
        </span>
    );

    return [
        {
            title: t('work_order_id'),
            template: renderWorkOrderID,
            width: '19%'
        },
        {
            title: t('client'),
            field: 'companyName',
            width: '21%'
        },
        {
            title: t('status'),
            template: renderStatus,
            width: '11%'
        },
        {
            title: t('priority'),
            template: renderPriority,
            width: '11%'
        },
        {
            title: t('modality_or_location'),
            template: renderModality,
            width: '20%'
        },
        {
            title: `${t('date_and_time')} (${timezoneSuffix})`,
            template: renderStartDateAndTime,
            width: '18%'
        }
    ];
};
